import _ from 'lodash'

import type { License } from '@img-arena/consoles-api-streaming'
import { streamingRestApi } from '@/service/streamingApi'
import getResponseErrorMessage from '@/utils/_legacy/getResponseErrorMessage'
import { notifyError } from '@/utils/notifyError'

/**
 * Fetches detailed license info for each license ID.
 * @param licenses Array of License objects
 * @param operatorId Operator ID
 * @param signal AbortSignal
 * @returns Map of license ID to detailed License object
 */
export async function fetchLicensesDetailsMap(
  licenses: License[],
  operatorId: number,
  signal: AbortSignal
): Promise<Map<number, License>> {
  const detailsResponses = await Promise.all(
    licenses.map(async ({ id: licenseId }) => {
      try {
        const { data } = await streamingRestApi.endpoints.operators.getLicense(licenseId, signal)
        return data ? data : ({} as License)
      } catch (err) {
        notifyError(
          `Failed to fetch license ${licenseId} for operator ${operatorId}: ${getResponseErrorMessage(err)}`
        )
        return {} as License
      }
    })
  )
  const detailsMap = new Map<number, License>()
  for (const details of detailsResponses) {
    if (details && details.id) {
      detailsMap.set(details.id, details)
    }
  }
  return detailsMap
}

/**
 * Merges base license data with detailed license data.
 * @param licenses Array of base License objects
 * @param detailsMap Map of license ID to detailed License object
 * @returns Array of merged License objects
 */
export function mergeLicensesWithDetails(
  licenses: License[],
  detailsMap: Map<number, License>
): License[] {
  return licenses.map((license) => {
    const details = detailsMap.get(license.id)
    return details ? _.merge({}, license, details) : license
  })
}
